import { Button } from '@edsn/pie-components';
import AddIcon from '@iconify/icons-material-symbols/add';
import asteriskIcon from '@iconify/icons-material-symbols/asterisk';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AddContactGroupModal } from '#pie/components/add-contactgroup-modal/AddContactGroupModal';
import { ExpandableList } from '#pie/components/expandable-list/ExpandableList';
import { ExpandableListItem } from '#pie/components/expandable-list/ExpandableListItem';
import { Icon } from '#pie/components/icon/Icon';
import { RemoveCprContactGroupButton } from '#pie/components/remove-cpr-contact-group-button/RemoveCprContactGroupButton';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { Tooltip } from '#pie/components/tooltip/Tooltip';
import type { CprContactGroupMembership } from '#pie/internal/api/pie-bff';

type ContactGroupSectionProps = {
  memberships: CprContactGroupMembership[];
  contactpersonId: string;
};

export const ContactGroupSection = ({
  memberships,
  contactpersonId,
}: ContactGroupSectionProps) => {
  const { t } = useTranslation();

  const [openModal, setOpenModal] = useState(false);

  const subunitGroups = Array.from(
    Map.groupBy(memberships, (m) => m.subunit.id).values(),
  ).map((memberships) => ({
    subunit: memberships[0].subunit,
    memberships,
  }));

  const uniqueSubunitCount = subunitGroups.length;
  const hasMultipleSubunits = subunitGroups.length > 1;

  return (
    <>
      <Stack className="mx-auto my-6 max-w-4xl">
        <div className="bg-white rounded-md px-6">
          <div className="flex flex-row justify-between items-center h-20 border-b border-border-minimal-enabled">
            <div className="flex flex-col">
              <Text variant="h6">{t('common.tab.contact_group')}</Text>
              {hasMultipleSubunits ? (
                <Text variant="sm" className="text-content-secondary">
                  <Trans
                    i18nKey="cpr_management.contact_groups.summary.multiple_subunits"
                    values={{
                      count: memberships.length,
                      subunitCount: uniqueSubunitCount,
                    }}
                  />
                </Text>
              ) : (
                <Text variant="sm" className="text-content-secondary">
                  <Trans
                    i18nKey="cpr_management.contact_groups.summary.single_subunit"
                    values={{
                      count: memberships.length,
                      subunitName: memberships?.[0].subunit.name,
                    }}
                  />
                </Text>
              )}
            </div>
            <Button
              action="tertiary"
              emphasis="subtle"
              size="sm"
              type="button"
              iconStart={
                <Icon
                  icon={AddIcon}
                  className="text-action-secondary-enabled"
                  size={20}
                />
              }
              onClick={() => setOpenModal(true)}
            >
              {t('common.contact_group')}
            </Button>
          </div>
          <Stack gap="md" className="px-4 pb-6">
            {hasMultipleSubunits ? (
              <ExpandableList>
                {subunitGroups.map((group) => (
                  <ExpandableListItem
                    key={group.subunit.id}
                    value={group.subunit.id}
                    title={group.subunit.name}
                    label={t('cpr_management.contact_groups.active_count', {
                      count: group.memberships.length,
                    })}
                  >
                    <ContactGroupList memberships={group.memberships} />
                  </ExpandableListItem>
                ))}
              </ExpandableList>
            ) : (
              <div className="pt-4">
                <ContactGroupList memberships={memberships} />
              </div>
            )}
          </Stack>
        </div>
      </Stack>

      <AddContactGroupModal
        open={openModal}
        onOpenChange={setOpenModal}
        contactPersonId={contactpersonId}
        activeMemberships={memberships}
      />
    </>
  );
};

function ContactGroupList({
  memberships,
}: {
  memberships: CprContactGroupMembership[];
}) {
  const { t } = useTranslation();
  const sortedGroups = useMemo(
    () =>
      [...memberships].sort((a, b) =>
        (a.contactGroup?.name ?? '').localeCompare(b.contactGroup?.name ?? ''),
      ),
    [memberships],
  );

  return (
    <>
      {sortedGroups.map((membership) => (
        <div
          className="flex flex-row justify-between items-center py-2"
          key={membership.id}
        >
          <div className="flex items-center gap-2">
            <Text>{membership.contactGroup?.name}</Text>

            {membership.contactGroup.isRequired && (
              <Tooltip
                message={t(
                  'cpr_management.contact_contactgroup.tooltip.required',
                )}
                variant="dark"
              >
                <div className="bg-support-help-light rounded py-0.5 px-1">
                  <Icon
                    icon={asteriskIcon}
                    size={16}
                    className="text-content-help"
                  />
                </div>
              </Tooltip>
            )}
          </div>
          <RemoveCprContactGroupButton membership={membership} />
        </div>
      ))}
    </>
  );
}
